/**
 * Store Availability Component
 * 
 * Custom element that displays store availability for a product variant.
 * Uses the Section Rendering API to fetch availability data when variant changes.
 */

if (!customElements.get('store-availability')) {
  customElements.define(
    'store-availability',
    class StoreAvailability extends HTMLElement {
      constructor() {
        super();
        
        this.errorHtml = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        
        // Only fetch if we have the required attributes
        if (this.dataset.variantId) {
          this.fetchAvailability(this.dataset.variantId);
        }
      }

      get rootUrl() {
        let url = this.dataset.rootUrl || '/';
        if (!url.endsWith('/')) {
          url = url + '/';
        }
        return url;
      }

      /**
       * Fetch store availability for a specific variant
       * @param {string|number} variantId - The variant ID to fetch availability for
       */
      fetchAvailability(variantId) {
        if (!variantId) return;
        
        const variantSectionUrl = `${this.rootUrl}variants/${variantId}/?section_id=store-availability`;

        fetch(variantSectionUrl)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.text();
          })
          .then((text) => {
            const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html').querySelector('.shopify-section');
            this.renderPreview(sectionInnerHTML);
          })
          .catch((e) => {
            console.error('Store availability fetch error:', e);
            const button = this.querySelector('button');
            if (button) button.removeEventListener('click', this.onClickRefreshList);
            this.renderError();
          });
      }

      onClickRefreshList(evt) {
        evt.preventDefault();
        this.fetchAvailability(this.dataset.variantId);
      }

      renderError() {
        if (!this.errorHtml) return;

        this.innerHTML = '';
        this.appendChild(this.errorHtml.cloneNode(true));

        const button = this.querySelector('button');
        if (button) {
          button.addEventListener('click', this.onClickRefreshList);
        }
      }

      renderPreview(sectionInnerHTML) {
        // Remove existing drawer if present
        const existingDrawer = document.querySelector('store-availability-drawer');
        if (existingDrawer) existingDrawer.remove();

        // Check if section has content
        const preview = sectionInnerHTML?.querySelector('store-availability-preview');
        if (!preview) {
          this.innerHTML = '';
          this.removeAttribute('available');
          return;
        }

        // Update preview content
        this.innerHTML = preview.outerHTML;
        this.setAttribute('available', '');

        // Append drawer to body
        const drawer = sectionInnerHTML.querySelector('store-availability-drawer');
        if (drawer) {
          document.body.appendChild(drawer);
        }

        // Set up event handlers
        this.setupDrawerButton();
        this.setupFavoriteStoreSelector();
        
        // Restore favorite store from localStorage
        this.restoreFavoriteStore();
      }

      setupDrawerButton() {
        const button = this.querySelector('a[href*="drawer"]');
        if (!button) return;

        const target = button.getAttribute('href').replace('#', '');

        button.addEventListener('click', (event) => {
          event.preventDefault();
          if (typeof Woolman !== 'undefined' && Woolman.ModalsAndDrawers) {
            Woolman.ModalsAndDrawers.showModalOrDrawer(target);
          }
        });
      }

      setupFavoriteStoreSelector() {
        const select = this.querySelector('[data-select-favorite-store]');
        if (!select) return;

        select.addEventListener('change', (event) => {
          const selectedStore = event.target.value;
          localStorage.setItem('current_store', selectedStore);
          this.updateFavoriteStoreStatus(selectedStore);
        });
      }

      restoreFavoriteStore() {
        const savedStore = localStorage.getItem('current_store');
        if (!savedStore) return;

        const select = this.querySelector('[data-select-favorite-store]');
        if (!select) return;

        // Try to select the saved store
        const option = select.querySelector(`option[value="${CSS.escape(savedStore)}"]`);
        if (option) {
          select.value = savedStore;
          this.updateFavoriteStoreStatus(savedStore);
        }
      }

      updateFavoriteStoreStatus(storeName) {
        const statusContainer = this.querySelector('[data-favorite-store-status]');
        if (!statusContainer) return;

        // Get store data from the preview element's data attribute
        const previewElement = this.querySelector('store-availability-preview');
        if (!previewElement) return;

        let storesData;
        try {
          storesData = JSON.parse(previewElement.dataset.stores || '[]');
        } catch (e) {
          console.error('Error parsing stores data:', e);
          return;
        }

        // Get labels from data attributes
        const labels = {
          available: previewElement.dataset.labelAvailable || 'Available',
          displayable: previewElement.dataset.labelDisplayable || 'Displayed in store',
          unavailable: previewElement.dataset.labelUnavailable || 'Ask for availability'
        };

        // Find the store and its status
        const store = storesData.find(s => s.name === storeName);
        if (!store) return;

        // Build status HTML with labels
        let statusHtml = '';
        const statusArray = Array.isArray(store.status) ? store.status : [store.status];
        
        statusArray.forEach(status => {
          const trimmedStatus = status.trim();
          let statusClass = 'store-availability-status--unavailable';
          let label = labels.unavailable;
          
          if (trimmedStatus === 'available') {
            statusClass = 'store-availability-status--available';
            label = labels.available;
          } else if (trimmedStatus === 'displayable') {
            statusClass = 'store-availability-status--displayable';
            label = labels.displayable;
          }
          
          statusHtml += `<span class="store-availability-favorite__status-item">
            <span class="store-availability-status ${statusClass}"></span>
            <span>${label}</span>
          </span>`;
        });

        statusContainer.innerHTML = statusHtml;
      }
    }
  );
}

if (!customElements.get('store-availability-drawer')) {
  customElements.define(
    'store-availability-drawer',
    class StoreAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        // Set up close buttons
        const closeBtns = this.querySelectorAll('[data-close]');
        if (closeBtns) {
          closeBtns.forEach((closeBtn) => {
            closeBtn.addEventListener('click', this.handleClose.bind(this));
          });
        }

        // Set up overlay click to close
        const overlay = this.querySelector('[data-overlay]');
        if (overlay) {
          overlay.addEventListener('click', this.handleClose.bind(this));
        }
      }

      handleClose(event) {
        event.preventDefault();
        if (typeof Woolman !== 'undefined' && Woolman.ModalsAndDrawers) {
          Woolman.ModalsAndDrawers.closeModalOrDrawerOrDrawerFromEvent(event);
        }
      }
    }
  );
}
